<template>
  <div class="actions-parcours">
    <div class="ap-ligne">
      <span v-if="badge" :class="['ap-pastille', 'ap-niveau-' + badge.niveau]" :title="'Depuis ' + badge.depuis">
        <i class="fa fa-map-marker-alt" aria-hidden="true"></i> {{ badge.libelle }} · {{ badge.niveau === 'sorti' ? '' : 'depuis ' }}{{ badge.depuis }}
      </span>
      <span v-else class="ap-pastille ap-niveau-sorti">Aucune venue enregistrée</span>
      <slot></slot>
      <button v-if="actions.transferer && !panneau" type="button" class="btn btn-primary ap-bouton" @click="ouvrirTransfert">
        <i class="fa fa-exchange-alt" aria-hidden="true"></i> Transférer
      </button>
      <button v-if="actions.confirmer_arrivee" type="button" class="btn btn-success ap-bouton" :disabled="envoi" @click="confirmerArrivee">
        Confirmer l’arrivée en {{ transfert.vers }}
      </button>
      <template v-if="actions.annuler_transfert">
        <button v-if="!confirmerAnnulation" type="button" class="btn btn-outline-danger ap-bouton" @click="confirmerAnnulation = true">Annuler le transfert</button>
        <span v-else class="ap-confirmation">Annuler le transfert vers {{ transfert.vers }} ?
          <button type="button" class="btn btn-danger ap-bouton" :disabled="envoi" @click="annulerTransfert">Oui</button>
          <button type="button" class="btn btn-secondary-light-o ap-bouton" @click="confirmerAnnulation = false">Non</button>
        </span>
      </template>
      <!-- Détails du transfert et du lit : repliés par défaut (04/10/2026). -->
      <button v-if="aDetails" type="button" class="ap-plier" :aria-expanded="details ? 'true' : 'false'" @click="details = !details">
        <i :class="['fa', details ? 'fa-chevron-up' : 'fa-chevron-down']" aria-hidden="true"></i> {{ details ? 'Masquer' : 'Détails' }}
      </button>
    </div>
    <div v-if="aDetails && details" class="ap-details">
      <p v-if="transfert" class="ap-transfert">
        Transfert demandé{{ transfert.par ? ' par ' + transfert.par : '' }} à {{ heure(transfert.depuis) }} vers <strong>{{ transfert.vers }}</strong><template v-if="transfert.motif"> · {{ transfert.motif }}</template>.
        <template v-if="!actions.confirmer_arrivee">L’infirmier du service confirmera l’arrivée.</template>
      </p>
      <p v-if="litGarde" class="ap-lit">
        Lit d’hospitalisation gardé : {{ libelleChambre(donnees.sejour.chambre) }}<template v-if="donnees.sejour.lit"> · {{ donnees.sejour.lit }}</template>
      </p>
    </div>

    <!-- Transfert : le médecin choisit le service d'arrivée ; le lit d'hospitalisation est gardé. -->
    <div v-if="panneau" class="ap-panneau" @keydown.enter.prevent>
      <strong class="d-block mb-2">Transférer vers</strong>
      <p v-if="panneau.chargement" class="text-grey">Chargement des unités de soins…</p>
      <p v-else-if="!panneau.services.length" class="text-grey">Aucune autre unité de soins active. Créez-la dans Paramètres › Unités de soins (ex. Bloc opératoire, Réanimation, Salle de réveil).</p>
      <div v-else class="ap-choix" role="radiogroup" aria-label="Service d’arrivée">
        <button v-for="service in panneau.services" :key="service.id" type="button" role="radio" :aria-checked="panneau.service_id === service.id ? 'true' : 'false'"
          :class="{ choisi: panneau.service_id === service.id }" @click="panneau.service_id = service.id">{{ service.name }}</button>
      </div>
      <label class="ap-motif" for="ap-motif">Motif (facultatif)</label>
      <input id="ap-motif" v-model.trim="panneau.motif" type="text" maxlength="255" class="input-secondary-light" placeholder="Ex. intervention programmée, surveillance post-opératoire…">
      <p v-if="panneau.erreur" class="text-danger" role="alert">{{ panneau.erreur }}</p>
      <div class="ap-boutons">
        <button type="button" class="btn btn-secondary-light-o btn-radius" @click="panneau = null">Annuler</button>
        <button type="button" class="btn btn-primary btn-radius" :disabled="!panneau.service_id || envoi" @click="transferer">
          {{ envoi ? 'Transfert…' : 'Transférer vers ' + nomService(panneau.service_id) }}
        </button>
      </div>
    </div>
    <p v-if="erreur" class="text-danger ap-message" role="alert">{{ erreur }}</p>
  </div>
</template>

<script>
import api from '@/api/consultations/_parcours'
import { pastille, heure, libelleChambre } from './parcours-utils'

function messageErreur(erreur, defaut) {
  const donnees = erreur && erreur.response ? erreur.response.data : null
  return (donnees && donnees.message) || defaut
}

// Où est le patient et depuis quand ; transfert (médecin), annulation (médecin), arrivée confirmée (infirmier).
export default {
  name: 'actions-parcours',
  props: {
    donnees: { type: Object, default: null },
    maintenant: { type: Date, default: () => new Date() },
  },
  data() {
    return { panneau: null, envoi: false, erreur: '', confirmerAnnulation: false, details: false }
  },
  computed: {
    badge() { return this.donnees ? pastille(this.donnees.position, this.maintenant) : null },
    actions() { return (this.donnees && this.donnees.actions) || {} },
    transfert() { return this.donnees ? this.donnees.transfert_en_cours : null },
    litGarde() { return !!(this.donnees && this.donnees.sejour && this.donnees.sejour.chambre && this.donnees.sejour.actif) },
    aDetails() { return !!this.transfert || this.litGarde },
  },
  watch: {
    donnees() { this.confirmerAnnulation = false },
  },
  methods: {
    heure(iso) { return heure(iso, this.maintenant) },
    libelleChambre,
    nomService(id) {
      const service = this.panneau ? this.panneau.services.find(s => Number(s.id) === Number(id)) : null
      return service ? service.name : '…'
    },
    async ouvrirTransfert() {
      this.erreur = ''
      const actuel = this.donnees && this.donnees.sejour ? Number(this.donnees.sejour.service_actuel_id) : 0
      this.panneau = { chargement: true, services: [], service_id: null, motif: '', erreur: '' }
      try {
        const reponse = await api.services()
        const liste = reponse && Array.isArray(reponse.data) ? reponse.data : []
        this.panneau.services = liste.filter(s => [true, 1, '1'].indexOf(s.is_active) >= 0 && Number(s.id) !== actuel)
      } catch (erreur) {
        this.panneau.erreur = 'Les unités de soins n’ont pas pu être chargées.'
      } finally {
        if (this.panneau) this.panneau.chargement = false
      }
    },
    async transferer() {
      const p = this.panneau
      if (!p || !p.service_id || !this.donnees || !this.donnees.sejour) return
      this.envoi = true
      p.erreur = ''
      try {
        const destination = this.nomService(p.service_id)
        await api.transferer(this.donnees.sejour.id, p.service_id, p.motif)
        this.panneau = null
        this.$emit('change', 'Transfert vers ' + destination + ' enregistré.')
      } catch (erreur) {
        p.erreur = messageErreur(erreur, 'Le transfert n’a pas pu être enregistré.')
      } finally {
        this.envoi = false
      }
    },
    async confirmerArrivee() {
      if (!this.transfert) return
      this.envoi = true
      this.erreur = ''
      try {
        await api.confirmerArrivee(this.transfert.id)
        this.$emit('change', 'Arrivée en ' + this.transfert.vers + ' confirmée.')
      } catch (erreur) {
        this.erreur = messageErreur(erreur, 'L’arrivée n’a pas pu être confirmée.')
      } finally {
        this.envoi = false
      }
    },
    async annulerTransfert() {
      if (!this.transfert) return
      this.envoi = true
      this.erreur = ''
      try {
        await api.annulerTransfert(this.transfert.id)
        this.confirmerAnnulation = false
        this.$emit('change', 'Transfert annulé.')
      } catch (erreur) {
        this.erreur = messageErreur(erreur, 'Le transfert n’a pas pu être annulé.')
      } finally {
        this.envoi = false
      }
    },
  },
}
</script>

<style scoped>
.ap-ligne { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.ap-pastille { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 999px; font-weight: 600; font-size: 14px; }
.ap-niveau-actif { background: #e7efff; color: #2457c5; }
.ap-niveau-normal { background: #eef3fb; color: var(--ui-text, #304269); }
.ap-niveau-orange { background: #fff1e0; color: #b25e09; }
.ap-niveau-rouge { background: #fdecec; color: #b42318; }
.ap-niveau-sorti { background: #f0f2f5; color: var(--ui-text-secondary, #65728b); }
.ap-transfert, .ap-lit { margin: 6px 0 0; color: var(--ui-text, #4a5875); font-size: 13px; }
.ap-details { margin-top: 2px; }
/* Boutons fins et arrondis, sur une ligne (y compris « Parcours » placé dans la ligne). */
.ap-ligne ::v-deep .ap-bouton { padding: 3px 14px; border-radius: 999px; border-width: 1px; font-size: 13px; line-height: 1.5; white-space: nowrap; }
.ap-plier { padding: 2px 6px; border: 0; background: transparent; color: #3b73f2; font-size: 13px; font-weight: 600; white-space: nowrap; }
.ap-plier:hover { text-decoration: underline; }
.ap-confirmation { display: inline-flex; align-items: center; gap: 6px; color: #b42318; font-size: 14px; }
.ap-panneau { margin-top: 12px; padding: 14px 16px; border: 1px solid #d7e3fa; border-radius: 12px; background: #f7faff; }
.ap-choix { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.ap-choix button { padding: 8px 14px; border: 1px solid #c9d7fc; border-radius: 999px; background: #fff; color: var(--ui-text, #304269); font-weight: 600; }
.ap-choix button.choisi { border-color: #3b73f2; background: #3b73f2; color: #fff; }
.ap-motif { display: block; margin: 4px 0; font-size: 14px; }
.ap-panneau input { width: 100%; max-width: 560px; }
.ap-boutons { display: flex; justify-content: flex-end; gap: 8px; margin-top: 10px; }
.ap-message { margin: 8px 0 0; }
</style>
